import { minutesToMilliseconds } from 'date-fns'
import { getPriceIdMap, GetTierFunction } from '.'
import { AiUserSubscription, BillingCycle } from '../../types'
import { BaseTier, CurrencySymbols, GetPriceIdType, TrainingSpeed } from './BaseTier'
import {
  BRAZILIAN_PRICING_CUTOFF_DATE,
  BRAZILIAN_PRICING_SUNSET_CUTOFF_DATE,
  CONVERTER_10_MIN_DOWNLOAD_CUTOFF_DATE,
  CONVERTER_15_DOLLAR_CUTOFF_DATE,
  CREATOR_75_CONVERTER_10_DOWNLOAD_CUTOFF_DATE
} from './constants'

export type ConverterTierType =
  | ConverterTier
  | Converter10Tier
  | Converter30Tier
  | ConverterBrazilTier

/**
 * NOTE: updates to this will effect Converter30Tier as well
 */
class ConverterTier extends BaseTier {
  readonly subscriptionType = AiUserSubscription.CONVERTER
  readonly subscriptionLevel = 2
  readonly vocalLimitMinutes = 10
  readonly downloadMsPerMonth = minutesToMilliseconds(15)
  readonly textToSpeechCharacterLimit = 2500
  readonly modelSlots = 2

  readonly inferenceSecondsPerMonth = 3600
  readonly advancedSettingsEnabled = true
  readonly artistVoiceModelsEnabled = true
  readonly textToSpeechCharacterConverts = 10_000

  readonly trainingSpeed = TrainingSpeed.PRIORITY
  readonly maxTrainingDatasetMb = 500
  readonly maxTrainDatasetFileSizeMb = 200
  readonly maxTrainingDatasetDurationMinutes = 60
  readonly maxYoutubeTrainingVideoLengthMinutes = 20
  readonly maxMlTrainingJobsPerMonth = Infinity
  readonly youtubeConvertsPerDay = Infinity

  readonly currencySymbol: CurrencySymbols = CurrencySymbols.USD
  readonly monthlyPrice: number = 11.99
  readonly annualPrice: number = 115

  getPriceId: GetPriceIdType = (billingCycle: BillingCycle, isProd: boolean) => {
    return getPriceIdMap(isProd, billingCycle).CONVERTER_PRICE
  }
}

/**
 * The tier for users that created accounts after BRAZILIAN_PRICING_CUTOFF_DATE and are in Brazil
 */
class ConverterBrazilTier extends ConverterTier {
  readonly currencySymbol = CurrencySymbols.BRL
  readonly monthlyPrice: number = 30
  readonly annualPrice: number = 288

  getPriceId: GetPriceIdType = (billingCycle: BillingCycle, isProd: boolean) => {
    return getPriceIdMap(isProd, billingCycle).CONVERTER_PRICE_BRAZIL
  }
}

/**
 * The tier for users that created accounts before CONVERTER_15_DOLLAR_CUTOFF_DATE
 */
class Converter15Tier extends ConverterTier {
  readonly monthlyPrice = 14.99
  readonly annualPrice = 144

  getPriceId: GetPriceIdType = (billingCycle: BillingCycle, isProd: boolean) => {
    return getPriceIdMap(isProd, billingCycle).CONVERTER_PRICE_15
  }
}

/**
 * The tier for users that created accounts before CONVERTER_10_MIN_DOWNLOAD_CUTOFF_DATE
 */
class Converter10Tier extends ConverterTier {
  readonly downloadMsPerMonth = minutesToMilliseconds(10)

  readonly monthlyPrice = 9.99
  readonly annualPrice = 96

  getPriceId: GetPriceIdType = (billingCycle: BillingCycle, isProd: boolean) => {
    return getPriceIdMap(isProd, billingCycle).CONVERTER_PRICE_10
  }
}

/**
 * The tier for users that created accounts before CREATOR_75_CONVERTER_10_DOWNLOAD_CUTOFF_DATE
 */
class Converter30Tier extends ConverterTier {
  readonly downloadMsPerMonth = minutesToMilliseconds(30)

  readonly monthlyPrice = 9.99
  readonly annualPrice = 96

  getPriceId: GetPriceIdType = (billingCycle: BillingCycle, isProd: boolean) => {
    return getPriceIdMap(isProd, billingCycle).CONVERTER_PRICE_10
  }
}

const converter15TierInstance = new Converter15Tier()
const converter10TierInstance = new Converter10Tier()
const converter30TierInstance = new Converter30Tier()
const converterTierInstance = new ConverterTier()
const converterBrazilTierInstance = new ConverterBrazilTier()

export const getUserConverterTier: GetTierFunction<ConverterTierType> = (user) => {
  if (!user) return converterTierInstance
  const createdAtDate = new Date(user.createdAt)

  const brazilSunsetCutoffDate = new Date(BRAZILIAN_PRICING_SUNSET_CUTOFF_DATE)
  const brazilCutoffDate = new Date(BRAZILIAN_PRICING_CUTOFF_DATE)
  if (
    createdAtDate > brazilCutoffDate &&
    createdAtDate < brazilSunsetCutoffDate &&
    user.countryCode === 'BR'
  ) {
    return converterBrazilTierInstance
  }

  const converter30CutoffDate = new Date(CREATOR_75_CONVERTER_10_DOWNLOAD_CUTOFF_DATE)
  if (createdAtDate < converter30CutoffDate) return converter30TierInstance

  const converter10CutoffDate = new Date(CONVERTER_10_MIN_DOWNLOAD_CUTOFF_DATE)
  if (createdAtDate < converter10CutoffDate) return converter10TierInstance

  const converter15CutoffDate = new Date(CONVERTER_15_DOLLAR_CUTOFF_DATE)
  if (createdAtDate < converter15CutoffDate) return converter15TierInstance

  return converterTierInstance
}

export {
  converter15TierInstance as Converter15Tier,
  converter10TierInstance as Converter10Tier,
  converter30TierInstance as Converter30Tier,
  converterTierInstance as ConverterTier,
  converterBrazilTierInstance as ConverterBrazilTier
}
